import { useEffect, useRef, useState } from 'react';
import {
  defaultTransportIssue,
  findTransportCase,
  findTransportCapability,
  findTransportEvidence,
  findTransportSource,
  transportCapabilities,
  transportCases,
  transportEvidence,
  transportEvidenceCategoryOrder,
  transportEvidenceGroupNote,
  transportEvidenceRelation,
  transportIssues,
  transportIssuesForCapability,
  transportSafetyNotice,
  type TransportCapability,
  type TransportCase,
  type TransportEvidence,
  type TransportEvidenceCategory,
  type TransportIssue,
} from './transport-research-content';
import type { DetailLocation } from './ScenarioDetailView';
import { ScenarioCardArt } from './ScenarioCardArt';
import { TransportResearchImage } from './TransportResearchImage';
import { handleScenarioTabsKeyDown } from './scenario-tab-keyboard';
import { modalDismissProps, useModalFocus } from './scenario-modal-focus';
import { platformRoutes } from '../../../shared/platform/platform-routes';

/**
 * 交通与地下空间事故详情页（SC-TRAFFIC-01）。
 *
 * 与高层建筑火灾、森林火灾、危化品事故共用同一套阅读结构：面包屑、场景头、两个页签、
 * 左侧问题栏、右侧单一内容面、抽屉。本页不接入源绑定投影，也不伪造一份 ScenarioProjection：
 * 内容来自 `transport-research-content.ts` 的公开资料研究参考。
 *
 * 这一页只做四件事：说明问题、列出需要掌握的信息、说明哪些技术能力可以帮助获取信息、
 * 给出可追溯的官方依据与案例。它不创建 Mission、不生成处置指令、不给出任何阈值或距离，
 * 也不把某次事故的地点说成适用范围内的地下事故。
 */
export function TransportScenarioDetailView({
  location,
  onLocation,
  onLibrary,
}: {
  readonly location: DetailLocation;
  readonly onLocation: (next: DetailLocation) => void;
  readonly onLibrary: () => void;
}) {
  const issue =
    transportIssues.find((row) => row.key === location.issue) ??
    transportIssues.find((row) => row.key === defaultTransportIssue) ??
    transportIssues[0];
  const [capability, setCapability] = useState<TransportCapability | null>(null);
  const [evidence, setEvidence] = useState<TransportEvidence | null>(null);
  const [all, setAll] = useState(false);
  const [category, setCategory] = useState<TransportEvidenceCategory | null>(null);
  const readingScroll = useRef(0);
  const previousTab = useRef(location.tab);
  const opener = useRef<HTMLElement | null>(null);
  const evidenceOpener = useRef<HTMLElement | null>(null);
  const information = useRef<HTMLElement>(null);
  const capabilities = useRef<HTMLElement>(null);
  useEffect(() => {
    setCapability(null);
    setEvidence(null);
    setAll(false);
    setCategory(null);
  }, [location.issue, location.tab]);
  useEffect(() => {
    if (location.tab !== 'issues' || !location.focus) return;
    (location.focus === 'information' ? information : capabilities).current?.scrollIntoView({
      block: 'start',
    });
  }, [location.issue, location.tab, location.focus]);
  useEffect(() => {
    if (previousTab.current === 'evidence' && location.tab === 'issues')
      window.scrollTo(0, readingScroll.current);
    if (previousTab.current === 'issues' && location.tab === 'evidence') window.scrollTo(0, 0);
    previousTab.current = location.tab;
  }, [location.tab]);
  if (!issue) return null;
  const changeTab = (tab: 'issues' | 'evidence') => {
    if (location.tab === 'issues' && tab === 'evidence') readingScroll.current = window.scrollY;
    onLocation({ ...location, tab, focus: null });
  };
  const related = issue.capabilityIds.flatMap((id) => {
    const row = findTransportCapability(id);
    return row ? [row] : [];
  });
  const evidenceRows = (all ? transportEvidence : issueEvidence(issue)).filter(
    (row) => category === null || row.category === category,
  );
  return (
    <section className="scc-detail scc-tr-detail" data-reference-nature="PUBLIC_RESEARCH_REFERENCE">
      <nav className="scc-breadcrumb" aria-label="面包屑">
        <button onClick={onLibrary}>场景中心</button>
        <span aria-hidden="true">›</span>
        <strong>交通与地下空间事故</strong>
      </nav>
      <header className="scc-scene-head">
        <div className="scc-scene-art">
          <ScenarioCardArt id="transport" name="交通与地下空间事故" />
        </div>
        <div className="scc-scene-title">
          <h1>交通与地下空间事故</h1>
          <p>
            隧道、地铁站与区间、地下车库与通道中的火灾烟气、事故阻塞、人员疏散、积水设施、结构与通信问题。
          </p>
        </div>
        <button className="scc-scene-back" onClick={onLibrary}>
          返回场景库 →
        </button>
      </header>
      <div
        className="scc-tab-bar"
        role="tablist"
        aria-label="场景详情"
        onKeyDown={handleScenarioTabsKeyDown}
      >
        {(['issues', 'evidence'] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            id={'scc-tr-tab-' + tab}
            aria-selected={location.tab === tab}
            aria-controls="scc-tr-panel"
            tabIndex={location.tab === tab ? 0 : -1}
            onClick={() => changeTab(tab)}
          >
            {tab === 'issues' ? '典型问题' : '依据与案例'}
          </button>
        ))}
      </div>
      <div id="scc-tr-panel" role="tabpanel" aria-labelledby={'scc-tr-tab-' + location.tab}>
        {location.tab === 'issues' ? (
          <div className="scc-issue-layout">
            <aside className="scc-issue-list" aria-label="典型问题">
              <h2>典型问题</h2>
              <ul>
                {transportIssues.map((row) => (
                  <li key={row.key}>
                    <button
                      data-issue={row.key}
                      aria-current={row.key === issue.key ? 'true' : undefined}
                      onClick={() => onLocation({ issue: row.key, tab: 'issues', focus: null })}
                    >
                      <span>{row.title}</span>
                      <span aria-hidden="true">›</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p>选择问题，查看相关信息与能力。</p>
            </aside>
            <div className="scc-issue-main">
              <article className="scc-issue-intro">
                <div>
                  <h2>{issue.title}</h2>
                  <p>{issue.summary}</p>
                  <p className="scc-issue-context">适用空间：{issue.context}</p>
                  <p className="scc-issue-impact">为什么需要关注：{issue.why}</p>
                  <ul className="scc-drawer-list scc-tr-questions">
                    {issue.questions.map((question) => (
                      <li key={question}>{question}</li>
                    ))}
                  </ul>
                </div>
                <TransportResearchImage file="hero-traffic-underground.webp" label={issue.title} />
              </article>
              <section className="scc-read-section" ref={information} tabIndex={-1}>
                <div className="scc-heading-row">
                  <h2>需要掌握的信息</h2>
                  <button className="scc-text-button" onClick={() => changeTab('evidence')}>
                    查看本问题依据 ↗
                  </button>
                </div>
                <p className="scc-section-lead">
                  以下是需要了解的信息，未表示当前已经取得；缺失的部分按“待核实”保留。每一行的“查看依据”打开与该信息对应的官方条款或报告章节。
                </p>
                <ul className="scc-info-rows" data-linked-module="information">
                  {issue.information.map((row) => (
                    <li className="scc-info-row" key={row.name}>
                      <div className="scc-info-row-main">
                        <strong>{row.name}</strong>
                        <p>需要获取：{row.what}</p>
                        <p>通常来源：{row.acquisition}</p>
                      </div>
                      <button
                        className="scc-text-button scc-info-evidence"
                        onClick={(event) => {
                          evidenceOpener.current = event.currentTarget;
                          setEvidence(findTransportEvidence(row.evidenceId) ?? null);
                        }}
                      >
                        查看依据 ↗
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section" ref={capabilities} tabIndex={-1}>
                <h2>所需能力（研究主题）</h2>
                <p className="scc-section-lead">
                  本场景共整理 {transportCapabilities.length}{' '}
                  项能力研究主题，此处显示与当前问题相关的 {related.length}{' '}
                  项。这些主题只说明可以怎样获取或核验信息，不代表本地已配备、已接入或获准使用。
                </p>
                <ul className="scc-cap-grid" data-linked-module="capability">
                  {related.map((row) => (
                    <li
                      className="scc-cap-card scc-tr-card"
                      key={row.id}
                      data-research-capability={row.id}
                    >
                      <div className="scc-cap-art scc-tr-art">
                        <TransportResearchImage file={row.image} label={row.title} />
                      </div>
                      <div className="scc-cap-body">
                        <strong>{row.title}</strong>
                        <span className="scc-cap-scope" data-relation-marker>
                          研究关联 · 待专业审核
                        </span>
                        <p className="scc-cap-purpose">{row.role}</p>
                        <p className="scc-cap-output">可获得：{row.outputs.join('、')}</p>
                        <button
                          className="scc-text-button"
                          onClick={(event) => {
                            opener.current = event.currentTarget;
                            setCapability(row);
                          }}
                        >
                          阅读能力说明 →
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="scc-read-section">
                <h2>本问题的官方依据</h2>
                <ul className="scc-tr-evidence-brief" data-evidence-brief data-issue={issue.id}>
                  {issueEvidence(issue).map((row) => (
                    <li key={row.id}>
                      <button
                        className="scc-text-button"
                        onClick={(event) => {
                          evidenceOpener.current = event.currentTarget;
                          setEvidence(row);
                        }}
                      >
                        {row.id} · {row.label}
                      </button>
                      <span className="scc-tr-evidence-loc">
                        {findTransportSource(row.sourceId)?.title}，{row.locator}
                      </span>
                    </li>
                  ))}
                </ul>
                <TransportCaseList ids={issue.caseIds} />
              </section>
              <footer className="scc-issue-footer">
                <span>所需能力仅为研究方向，尚无正式定义关联。</span>
                <button className="scc-primary" onClick={() => changeTab('evidence')}>
                  查看依据与案例
                </button>
              </footer>
            </div>
          </div>
        ) : (
          <section className="scc-evidence-page">
            <div className="scc-heading-row">
              <h2>依据与案例</h2>
              <div className="scc-evidence-switch" role="group" aria-label="资料范围">
                <button aria-pressed={!all} onClick={() => setAll(false)}>
                  与本问题相关
                </button>
                <button aria-pressed={all} onClick={() => setAll(true)}>
                  查看本场景全部依据
                </button>
              </div>
            </div>
            <p className="scc-section-lead">
              {all
                ? `本场景共 ${transportEvidence.length} 张依据卡，按来源性质分类。`
                : `与“${issue.title}”相关的 ${issueEvidence(issue).length} 张依据卡。`}
              每张卡片给出文件、准确条款或报告章节，以及不能据此推导的结论。
            </p>
            <div className="scc-evidence-switch" role="group" aria-label="资料类型">
              {([null, ...transportEvidenceCategoryOrder] as const).map((value) => (
                <button
                  key={value ?? 'all'}
                  aria-pressed={category === value}
                  onClick={() => setCategory(value)}
                >
                  {value ?? '全部类型'}
                </button>
              ))}
            </div>
            <TransportEvidenceGroups rows={evidenceRows} />
            {(!category || category === '真实事故案例' || category === '相关道路案例') && (
              <TransportCaseList ids={transportCases.map((row) => row.id)} grouped />
            )}
            <button className="scc-text-button" onClick={() => changeTab('issues')}>
              返回当前问题：{issue.title} ↑
            </button>
          </section>
        )}
      </div>
      <p className="scc-notice scc-chem-safety">{transportSafetyNotice}</p>
      {capability && (
        <TransportCapabilityDrawer
          capability={capability}
          issue={issue}
          restoreFocusTo={opener.current}
          onClose={() => setCapability(null)}
        />
      )}
      {evidence && (
        <TransportEvidenceDrawer
          evidence={evidence}
          issue={issue}
          restoreFocusTo={evidenceOpener.current}
          onClose={() => setEvidence(null)}
        />
      )}
    </section>
  );
}

function issueEvidence(issue: TransportIssue): readonly TransportEvidence[] {
  return issue.evidenceIds.flatMap((id) => {
    const row = findTransportEvidence(id);
    return row ? [row] : [];
  });
}

function evidenceIssues(id: string): readonly string[] {
  return transportIssues
    .filter((issue) => issue.evidenceIds.includes(id))
    .map((issue) => issue.title);
}

export function TransportEvidenceGroups({ rows }: { readonly rows: readonly TransportEvidence[] }) {
  if (rows.length === 0)
    return (
      <p className="scc-chem-empty">此范围暂无该类型的依据，可切换资料类型或查看本场景全部依据。</p>
    );
  return (
    <div data-evidence-kind="evidence">
      {transportEvidenceCategoryOrder.map((name) => {
        const grouped = rows.filter((row) => row.category === name);
        if (!grouped.length) return null;
        return (
          <section className="scc-evidence-group" data-evidence-group={name} key={name}>
            <h3>{name}</h3>
            <p className="scc-evidence-group-note">{transportEvidenceGroupNote[name]}</p>
            <div className="scc-evidence-grid">
              {grouped.map((row) => {
                const source = findTransportSource(row.sourceId);
                return (
                  <article className="scc-evidence-card" data-evidence-id={row.id} key={row.id}>
                    <span className="scc-chip">
                      {row.id} · {source?.org} · {source?.code}
                    </span>
                    <h3>{row.label}</h3>
                    <p>{row.plain}</p>
                    <p className="scc-evidence-locator">
                      准确位置：{source?.title}，{row.locator}
                    </p>
                    <p className="scc-evidence-use">不能据此推导：{row.cannot}</p>
                    <p className="scc-evidence-locator">
                      关联问题：{evidenceIssues(row.id).join('、')}
                    </p>
                    {source && (
                      <a href={source.url} target="_blank" rel="noopener noreferrer">
                        打开官方原文 ↗
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function TransportCaseList({
  ids,
  grouped = false,
}: {
  readonly ids: readonly string[];
  readonly grouped?: boolean;
}) {
  const rows = [...new Set(ids)].flatMap((id) => {
    const row = findTransportCase(id);
    return row ? [row] : [];
  });
  if (rows.length === 0) return null;
  return (
    <div className="scc-chem-cases" data-evidence-kind="case" aria-label="真实事故与相关道路案例">
      <p className="scc-section-lead">
        每个案例注明适用范围：道路、地铁与地下空间分别标识，地面事件不作为地下事故展示。
      </p>
      <div className="scc-evidence-grid">
        {rows.map((row) => (
          <TransportCaseCard item={row} key={row.id} showSection={grouped} />
        ))}
      </div>
    </div>
  );
}

function TransportCaseCard({
  item,
  showSection,
}: {
  readonly item: TransportCase;
  readonly showSection: boolean;
}) {
  return (
    <article className="scc-evidence-card" data-case-id={item.id}>
      <span className="scc-chip">
        {item.kind} · {item.date} · {item.location}
      </span>
      <h3>{item.title}</h3>
      <p className="scc-tr-scope" data-scope-label={item.scopeLabel}>
        适用性质：{item.scopeLabel}
      </p>
      <p>{item.factual}</p>
      {showSection && <p className="scc-evidence-locator">对应章节：{item.section}</p>}
      <p className="scc-evidence-locator">与本研究的关系：{item.relevance}</p>
      <p className="scc-evidence-use">{item.scopeNote}</p>
      <p className="scc-evidence-use">不能据此推导：{item.limit}</p>
      {item.sourceIds.map((id) => {
        const source = findTransportSource(id);
        return source ? (
          <a key={id} href={source.url} target="_blank" rel="noopener noreferrer">
            打开官方原文：{source.title} ↗
          </a>
        ) : null;
      })}
    </article>
  );
}

/**
 * 依据抽屉：本任务最重要的部分。
 *
 * 六段固定顺序：这条依据说明什么 / 文件与准确位置 / 为什么与当前问题有关 /
 * 真实案例怎样印证（有关系才显示）/ 不能从中推导什么 / 官方原文或 PDF。
 * 读者不需要认识 sourceId、版本字段或 JSON，就能知道规则是什么、为什么相关、去哪里核查。
 */
export function TransportEvidenceDrawer({
  evidence,
  issue,
  restoreFocusTo,
  onClose,
}: {
  readonly evidence: TransportEvidence;
  readonly issue: TransportIssue;
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo ?? null);
  const source = findTransportSource(evidence.sourceId);
  const cases = evidence.caseIds.flatMap((id) => {
    const row = findTransportCase(id);
    return row ? [row] : [];
  });
  return (
    <dialog
      className="scc-drawer scc-information-drawer scc-tr-evidence-drawer"
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scc-tr-evidence-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">
            交通与地下空间事故 · {issue.title} · 依据 {evidence.id}
          </p>
          <h2 id="scc-tr-evidence-title">{evidence.label}</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        <section className="scc-drawer-section">
          <h3>这条依据说明什么</h3>
          <p>{evidence.plain}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>文件与准确位置</h3>
          <ul className="scc-drawer-list">
            <li>文件名称：{source?.title ?? '来源未登记'}</li>
            <li>发布机关：{source?.org ?? '来源未登记'}</li>
            <li>文号：{source?.code ?? '来源未登记'}</li>
            <li>
              准确位置：{evidence.locator}
              {source ? `（${source.kind}，${source.published} 发布）` : ''}
            </li>
            {source && <li>版本状态：{source.status}</li>}
          </ul>
        </section>
        <section className="scc-drawer-section">
          <h3>为什么与当前问题有关</h3>
          <p>{transportEvidenceRelation(issue.id, evidence.id)}</p>
          <p className="scc-drawer-note">当前问题：{issue.title}</p>
        </section>
        {cases.length > 0 && (
          <section className="scc-drawer-section">
            <h3>真实案例怎样印证</h3>
            {cases.map((row) => (
              <article className="scc-tr-case-note" data-drawer-case={row.id} key={row.id}>
                <strong>{row.title}</strong>
                <p className="scc-tr-scope">适用性质：{row.scopeLabel}</p>
                <p>{row.factual}</p>
                <p className="scc-evidence-locator">对应章节：{row.section}</p>
                <p className="scc-evidence-use">{row.scopeNote}</p>
              </article>
            ))}
          </section>
        )}
        <section className="scc-drawer-section">
          <h3>不能从中推导什么</h3>
          <p>{evidence.cannot}</p>
          {source && <p className="scc-drawer-note">适用范围：{source.scope}</p>}
        </section>
        <section className="scc-drawer-section">
          <h3>查看官方原文或 PDF</h3>
          {source ? (
            <>
              <a href={source.url} target="_blank" rel="noopener noreferrer">
                阅读官方原文 ↗
              </a>
              {source.documentUrl && (
                <p>
                  <a href={source.documentUrl} target="_blank" rel="noopener noreferrer">
                    {source.documentLabel ?? '官方 PDF'} ↗
                  </a>
                </p>
              )}
            </>
          ) : (
            <p className="scc-drawer-note">来源登记缺失，不提供外链。</p>
          )}
        </section>
        <p className="scc-drawer-note">
          官方原文外链；目标网络与 PDF 可达性未验证。本页未随包收录官方 PDF 原件。
        </p>
      </div>
    </dialog>
  );
}

export function TransportCapabilityDrawer({
  capability,
  issue,
  restoreFocusTo,
  onClose,
}: {
  readonly capability: TransportCapability;
  readonly issue: TransportIssue;
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo ?? null);
  const relatedIssues = transportIssuesForCapability(capability.id);
  return (
    <dialog
      className="scc-drawer scc-capability-drawer scc-chem-drawer"
      ref={dialog}
      aria-modal="true"
      aria-labelledby="scc-tr-capability-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">研究说明 · 交通与地下空间事故 · {issue.title}</p>
          <h2 id="scc-tr-capability-title">{capability.title}</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        <div className="scc-tr-drawer-hero">
          <TransportResearchImage file={capability.image} label={capability.title} />
        </div>
        <p className="scc-notice" data-capability-status="RESEARCH_TOPIC_ONLY">
          这是一项能力研究主题说明，不是正式 Capability 定义，也不表示设备已配备、已验证或已接入。
        </p>
        <section className="scc-drawer-section">
          <h3>它在获取信息中起什么作用</h3>
          <p>{capability.role}</p>
        </section>
        <div className="scc-chem-drawer-columns">
          <section className="scc-drawer-section">
            <h3>需要什么输入</h3>
            <ul className="scc-drawer-list">
              {capability.inputs.map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
          </section>
          <section className="scc-drawer-section">
            <h3>能提供什么输出</h3>
            <ul className="scc-drawer-list">
              {capability.outputs.map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
          </section>
        </div>
        <section className="scc-drawer-section">
          <h3>适用条件与限制</h3>
          <p>{capability.limitation}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>关联的典型问题</h3>
          <ul className="scc-drawer-list">
            {relatedIssues.map((title) => (
              <li key={title}>
                {title}
                {title === issue.title && <span className="scc-drawer-note"> · 当前问题</span>}
              </li>
            ))}
          </ul>
        </section>
        <p className="scc-drawer-footnote" data-definition-status="NO_APPROVED_MAPPING">
          能力定义暂不可查看：该研究主题尚无专业 Owner 批准的正式能力 ID 映射。
        </p>
        <a href={platformRoutes.equipment}>进入能力中心浏览／查找 →</a>
        <p className="scc-drawer-footnote">通用浏览入口，尚未精确匹配能力定义。</p>
      </div>
    </dialog>
  );
}
